import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useField } from '@unform/core';
import { InputCodeContainer, InputCodeItem } from './styles';

type InputCodeProps = {
  name: string;
  length?: number;
};

export const InputCode: React.FC<InputCodeProps> = ({ name, length = 6 }) => {
  const { fieldName, defaultValue, registerField } = useField(name);
  const fieldIds = useMemo(
    () => Array.from({ length }, (_, i) => `digit-${i + 1}`),
    [length],
  );

  const sanitizeToArray = React.useCallback(
    (code: string) => {
      const digits = (code ?? '').replace(/\D/g, '').slice(0, length).split('');
      const arr = Array(length).fill('');
      for (let i = 0; i < digits.length; i += 1) {
        arr[i] = digits[i];
      }
      return arr;
    },
    [length],
  );

  const [values, setValues] = useState<string[]>(() =>
    sanitizeToArray(String(defaultValue ?? '')),
  );
  const valuesRef = useRef(values);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: containerRef.current,
      getValue: () => valuesRef.current.join(''),
      setValue: (_, value) => {
        setValues(sanitizeToArray(String(value ?? '')));
      },
      clearValue: () => {
        setValues(Array(length).fill(''));
      },
    });
  }, [fieldName, length, registerField, sanitizeToArray]);

  const handleChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const onlyDigit = e.target.value.replace(/\D/g, '').slice(0, 1);
    setValues((prev) => {
      const next = [...prev];
      next[index] = onlyDigit;
      return next;
    });
    if (onlyDigit && index < fieldIds.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown =
    (index: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Voltar se o campo estiver vazio
      if (e.key === 'Backspace' && !values[index] && index > 0) {
        e.preventDefault(); // opcional
        inputRefs.current[index - 1]?.focus();
        return;
      }

      const controlKey =
        e.key === 'Backspace' ||
        e.key === 'Delete' ||
        e.key === 'Tab' ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight' ||
        e.key === 'Home' ||
        e.key === 'End' ||
        e.ctrlKey ||
        e.metaKey;

      if (controlKey) return;
      if (!/^[0-9]$/.test(e.key)) {
        e.preventDefault();
      }
    };

  const handlePaste = (
    index: number,
    e: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').replace(/\D/g, '');
    if (!text) return;
    setValues((prev) => {
      const next = [...prev];
      next[index] = text[0] ?? '';
      return next;
    });
  };

  return (
    <InputCodeContainer ref={containerRef}>
      {fieldIds.map((id, index) => (
        <InputCodeItem
          key={id}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={values[index]}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={handleKeyDown(index)}
          onPaste={(e) => handlePaste(index, e)}
          aria-label={`Dígito ${index + 1} do código`}
        />
      ))}
    </InputCodeContainer>
  );
};
